import {Injectable} from '@angular/core';
import { IDiagnosticOrder } from '@ispm/test/interfaces/diagnostic-order.interface';
import { ReplaySubject } from 'rxjs';
import { get, isEmpty } from 'lodash';
import { HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { CoreActionCreatorService } from '@ispm/core/services/core-action-creator.service';
import { Actions } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { IgenomicsState } from '@ispm/core/interfaces/genomics-state.interface';
import { IWorkflow } from '@ispm/core/interfaces/workflow.interface';
import { WorkflowConfigService } from '@ispm/workflow/services/workflow-config.service';
import { ResourceDataItem } from '@ispm/core/classes/resource-data-item.class';
import { UpdateResourcesAction } from '@ispm/shared/store/actions/test-bar.actions';
import { take } from 'rxjs/operators';
import { currentOrderWorkflow } from '../store/current-order.selector';
import { WorkflowService } from '@ispm/workflow/services/workflow.service';
import { TestService } from '@ispm/dashboard/test/services/test.service';
import { NotificationMessageService } from '@ispm/core/components/customise-notification/notifications-message.service';
import {IActionToDispatch} from "@ispm/shared/interfaces/action.interface";
import {ActivatedRoute, Router} from "@angular/router";
import {RouterManagerService} from "@ispm/core/services/router-manager.service";

@Injectable()
export class PollJobService {

  private statusFailed = 'Failed';
  private _RETRY_INTERVAL = 600;

  constructor(
    private actions$: Actions,
    private store$: Store<IgenomicsState>,
    private workflowConfigService: WorkflowConfigService,
    private testService: TestService,
    private workflowService: WorkflowService,
    private notificationMessageService: NotificationMessageService,
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private coreActionCreatorService: CoreActionCreatorService,
    private routerManagerService: RouterManagerService

  ) {
  }

  async performWorkflowStepByStepName(stepName: string, inputButtonArgs: any, currentTest: IDiagnosticOrder, targetStepForPolling?: string, flowConfig = 'async', workflowActionsPayload?: any, callback?: any, moveToStep: boolean = true, actionToDispatchOnError: IActionToDispatch = null) {
    this.coreActionCreatorService.setLoader(true);

    const subject: ReplaySubject<string> = new ReplaySubject(1);

    this.workflowConfigService.performWorkflowStep(currentTest, inputButtonArgs, { workflowActionsPayload}, false, null, actionToDispatchOnError).toPromise()
      .then(
        (jobResponse: HttpResponse<{ data: ResourceDataItem[], message: string}>) => {
          const updatedData = jobResponse.body.data;
          const updatedWorkflow = this.getWorkflowFromUpdatedData(updatedData);

          if (get(jobResponse, 'status', false)) {
            if (targetStepForPolling) {
              this.pollingUntilNextStep(targetStepForPolling, updatedWorkflow, flowConfig, updatedData, callback);
            } else {
              this.onSuccessNextStep(updatedWorkflow, flowConfig, updatedData, callback, moveToStep);
            }
          } else {
            this.onFailedNextStep(updatedWorkflow);
          }

          this.coreActionCreatorService.setLoader(false);
        },
        (err: HttpErrorResponse) => {
          const data = err['data'];
          const updatedWorkflow = this.getWorkflowFromUpdatedData(data);
          this.onFailedNextStep(updatedWorkflow);
          subject.complete();
        }
      );

    return subject.asObservable();
  }

  pollingUntilStep( revisionId, workflowId, testConfiguration, callback?, stepToWaitForInitial: string = '') {
    if (!!workflowId && !!revisionId) {
      if (!testConfiguration.initStepToWait && !stepToWaitForInitial) {
        if (callback && typeof callback === 'function') {
          callback();
        }
      }
      else {
        // If the cached workflow step is the needed step, no need to request for the workflow from the server
        const workflowData = { };
        this.store$.select(currentOrderWorkflow).pipe(take(1)).subscribe(orderWorkflow => workflowData['data'] = orderWorkflow);
        let workflowPromise;
        if (workflowData && workflowData['data']) {
          workflowPromise = new Promise(function (resolve) {
            resolve(workflowData);
          });
        }
        const workflowDataObs = workflowData && workflowData['data'] ? workflowPromise : this.workflowService.getWorklfowById(workflowId);
        workflowDataObs.then(currentWorkflow => {
          const currentWorkflowData = currentWorkflow.data ? currentWorkflow.data : currentWorkflow.body;
          const stepToWaitFor = this.findInitStepToWait(testConfiguration, currentWorkflowData, stepToWaitForInitial);
          if (!!stepToWaitFor && !!currentWorkflow && !!currentWorkflow.body && currentWorkflow.body.activeJobName !== stepToWaitFor) {
            this.waitForWorkflowCondition(workflowId, revisionId, workflow => workflow.activeJobName === stepToWaitFor)
              .then(() => callback())
              .catch(error => {
                console.error(error);
                throw new Error('Can\'t move to next step. ' + error.message);
              })
            ;
          } else if (callback && typeof callback === 'function') {
            callback();
          }
        }).catch(() => { });
      }
    }
  }

  pollingUntilStepAndNavigate( revisionId, workflowId, testConfiguration, navigateTo, navigationParams, navigationOptions?) {
    this.pollingUntilStep( revisionId, workflowId, testConfiguration, () => {
      this.router.navigate([navigateTo], {queryParams: navigationParams, fragment: navigationOptions?.fragment, skipLocationChange: navigationOptions?.skipLocationChange});
    });
  }

  public async waitForWorkflowCondition(
    workflowId: string, revisionId: string, waitUntil: (workflow: IWorkflow) => boolean,
    retryInterval?: number, maxRetries?: number
  ): Promise<IWorkflow> {
    if (maxRetries === 0) {
      return Promise.reject('Retries limit is reached.');
    }
    if (!isNaN(maxRetries)) {
      maxRetries--;
    }
    return this.workflowService.getWorklfowById(workflowId)
      .then(currentWorkflow => {
        const workflow = currentWorkflow.body;

        if (!this.isWorkflowRequestFailed(workflow, revisionId)) {
          if (!waitUntil(workflow)) {
            return new Promise((resolve, reject) => {
              setTimeout(() => {
                this.waitForWorkflowCondition(workflowId, revisionId, waitUntil, retryInterval, maxRetries)
                  .then(resolve).catch(reject);
              }, retryInterval || this.RETRY_INTERVAL);
            });
          }
          return Promise.resolve(workflow);
        }
        return Promise.reject('WorkflowRequest status is Failed.');
      });
  }

  private findInitStepToWait(testConfiguration, workflowRequest, stepToWaitFor: string = '') {
    const initStepToWait = stepToWaitFor ? stepToWaitFor : testConfiguration.initStepToWait;

    if (!!initStepToWait) {
      // check if we AFTER this step
      const revision = workflowRequest.revisions.find(rev => rev.id === workflowRequest.activeRevision);

      if (revision) {
        const currentStepId = revision.items.findIndex(item => {
          let type = item.type;

          // because for this step the type and the active job name have different value
          // but must be the same
          if (item.type.toLowerCase() === 'finalreview') {
            type = 'Final review';
          }

          return type === workflowRequest.activeJobName;
        });

        let targetStepId = revision.items.findIndex(item => item.type === initStepToWait);

        if (initStepToWait.toLowerCase() === 'none') { // test complete
          targetStepId =  currentStepId + 1;
        }

        return currentStepId >= 0 && targetStepId >= 0  && targetStepId > currentStepId ? initStepToWait : undefined;
      }
    }

    return undefined;
  }

  private getWorkflowFromUpdatedData(updatedData: ResourceDataItem[]) {
    if (!Array.isArray(updatedData)) {
      return;
    }
    const updatedWorkflowIndex = updatedData.findIndex(item => item.resourceType.toLowerCase() === 'workflowrequest');
    let updatedWorkflow;
    if (updatedWorkflowIndex > -1) {
      updatedWorkflow = get(updatedData[updatedWorkflowIndex], 'resource');
      updatedData.splice(updatedWorkflowIndex, 1);
    }
    return updatedWorkflow;
  }

  private pollingUntilNextStep(destination, updatedWorkflow?: IWorkflow, flowConfig = 'async', updatedData?: ResourceDataItem[], callback?: any) {
    const workflowId = this.routerManagerService.parameters.testId;
    const updatedWorkflow$: any = updatedWorkflow
      ? Promise.resolve({ data: updatedWorkflow})
      : this.workflowService.getWorklfowById(workflowId);

    updatedWorkflow$.then(currentWorkflow => {
      const data = currentWorkflow.body;
      const currentJob = data.activeJobName;
      if (this.isWorkflowRequestFailed(data)) {
        this.onFailedNextStep(currentWorkflow);
      } else {
        if (currentJob !== destination) {
          setTimeout(() => {
            this.pollingUntilNextStep(destination);
          }, this.RETRY_INTERVAL);
        } else {
          this.onSuccessNextStep(currentWorkflow, flowConfig, updatedData, callback);
        }
      }
    });
  }

  private isWorkflowRequestFailed(activeWorkflow, revisionId?: any) {
    revisionId = revisionId ? revisionId : this.routerManagerService.parameters.revisionId;
    const revision = activeWorkflow.revisions.find(revision => {
      return revision.id == revisionId;
    });
    return revision.status === this.statusFailed;
  }

  private onSuccessNextStep(workflow, flowConfig, updatedData: ResourceDataItem[], callback?, moveToStep: boolean = true) {
    if (moveToStep) {
      const testConfiguration = this.testService.testConfiguration;
      switch (flowConfig.toLowerCase()) {
        case 'sync': {
          this.workflowConfigService.setActiveWorkflow(workflow);
          if (updatedData) {
            this.store$.dispatch(new UpdateResourcesAction(updatedData));
          }
          const loadAllDiagnosticOrderInSyncFlow = !!testConfiguration.loadAllDiagnosticOrderInSyncFlow;
          if (loadAllDiagnosticOrderInSyncFlow) {
            this.workflowService.getAllPatientTests(this.routerManagerService.parameters);
          }
          break;
        }
        case 'async': {
          const parentState = this.router.url;
          const params = this.routerManagerService.parameters;
          if (parentState && params) {
            this.routerManagerService.reload();
            let enablePostReportToEmrGrowl = false;
            if (testConfiguration && testConfiguration.taskDispatchTemplates
              && !isEmpty(testConfiguration.taskDispatchTemplates)) {
              const postEmrTemplate = testConfiguration.taskDispatchTemplates
                .filter(e => e
                  && e.type
                  && e.type == 'postReportToEMR'
                  && e.reference
                  && e.settings
                  && get(e, 'settings.releaseFinal')
                  && get(e, 'settings.growlMessageForPostReportToEMR'));
              enablePostReportToEmrGrowl = postEmrTemplate.length === 1;
            }
            if (enablePostReportToEmrGrowl && get(updatedData, 'resource.activeJobName') === 'None' && moveToStep) {
              this.notificationMessageService.success('Successfully moved to next step and Report was successfully scheduled for posting to EMR');
            } else {
              this.notificationMessageService.success('Successfully moved to next step');
            }
          } else {
            this.notificationMessageService.error('Unable to move to the next step');
          }
          break;
        }
      }
    }
    this.coreActionCreatorService.setLoader(false);
    if (callback && typeof callback === 'function') {
      callback(updatedData);
    }

  }

  private onFailedNextStep(workflow?: IWorkflow) {
    if (workflow) {
      this.workflowConfigService.setActiveWorkflow(workflow);
    }
    this.coreActionCreatorService.setLoader(false);
    this.notificationMessageService.error('Unable to move to the next step');
  }

  get RETRY_INTERVAL(): number {
    return this._RETRY_INTERVAL;
  }

}


